import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	Link,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import { BASE_API_PATH } from "helpers/constants";
import DropUpload from "components/Elements/DropUploadBox";
import AddDialogStyle from "styles/application/AddDialogStyle";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import WarningMessageWithErrorIcon from "components/Elements/WarningMessageWithErrorIcon";
import { useDispatch } from "react-redux";
import { showNotications } from "../../../../../redux/notification/actions";
import { showError } from "../../../../../redux/common/actions";
import {
	downloadDefectPartsCSVTemplate,
	importDefectParts,
} from "services/defects/details";

const AT = AddDialogStyle();

const EMPTY_DRAFT = { name: "", qty: "", description: "" };

const manualPillStyle = {
	display: "inline-block",
	padding: "2px 10px",
	borderRadius: 12,
	fontSize: "12px",
	fontWeight: 600,
	background: "#d5d5d5",
	color: "#393939",
	whiteSpace: "nowrap",
};

const useStyles = makeStyles()(() => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
	},
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 5,
		marginBottom: 15,
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
	},
	sectionRow: {
		backgroundColor: "#f0f0f0",
	},
	sectionLabel: {
		fontWeight: 700,
		fontSize: 12,
		color: "#545353",
		letterSpacing: "0.05em",
	},
	rowError: { backgroundColor: "#fde8e8" },
	rowWarning: { backgroundColor: "#fff8e1" },
	editingRow: { backgroundColor: "#e8f4fd" },
	previewActions: {
		display: "flex",
		justifyContent: "flex-end",
		gap: 10,
		marginTop: 16,
		alignItems: "center",
	},
	selectionCount: {
		fontSize: 14,
		color: "#545353",
		marginRight: "auto",
	},
	fileError: {
		marginTop: 10,
		padding: "10px 14px",
		backgroundColor: "#fde8e8",
		border: "1px solid #f5c6cb",
		borderRadius: 4,
		color: "#721c24",
		fontSize: 14,
	},
	addRowButton: {
		marginTop: 8,
		fontSize: 13,
		color: "#307AD6",
		cursor: "pointer",
		background: "none",
		border: "none",
		padding: "4px 0",
		"&:hover": { textDecoration: "underline" },
	},
	actionCell: {
		display: "flex",
		alignItems: "center",
		gap: 2,
	},
}));

const hasErrors = (row) => Array.isArray(row.errors) && row.errors.length > 0;
const hasWarnings = (row) =>
	Array.isArray(row.warnings) && row.warnings.length > 0;

const ImportPartsDialog = ({ open, handleClose, defectId, importSuccess }) => {
	const { t } = useTranslation();
	const { classes } = useStyles();
	const dispatch = useDispatch();

	// upload step state
	const [uploadPercentCompleted, setUploadPercentCompleted] = useState(0);
	const [loading, setLoading] = useState(false);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [showProgress, setShowProgress] = useState(true);
	const [fileError, setFileError] = useState(null);
	const cancelFileUpload = useRef(null);
	const isCancelled = useRef(false);

	// preview step state
	const [view, setView] = useState("upload");
	const [previewRows, setPreviewRows] = useState([]);
	const [selectedIndices, setSelectedIndices] = useState(new Set());
	const [convertedCsvIndices, setConvertedCsvIndices] = useState(new Set());
	const [s3Key, setS3Key] = useState(null);

	// manual rows (added or edited-from-csv)
	const [manualRows, setManualRows] = useState([]);

	// inline editing
	const [editingRow, setEditingRow] = useState(null); // { source: 'csv'|'manual', index: number, isNew: boolean }
	const [draftValues, setDraftValues] = useState(EMPTY_DRAFT);

	const resetState = () => {
		setView("upload");
		setFileError(null);
		setPreviewRows([]);
		setSelectedIndices(new Set());
		setConvertedCsvIndices(new Set());
		setS3Key(null);
		setManualRows([]);
		setEditingRow(null);
		setDraftValues(EMPTY_DRAFT);
		setUploadPercentCompleted(0);
		setLoading(false);
		setFetchLoading(false);
		setShowProgress(true);
	};

	const closeOverride = () => {
		handleClose();
		resetState();
	};

	// ── Download template ────────────────────────────────────────────────────

	const downloadTemplate = async () => {
		try {
			const response = await downloadDefectPartsCSVTemplate(defectId);
			if (response.status && response.data) {
				const element = document.createElement("a");
				const file = new Blob([response.data], { type: "text/csv" });
				element.href = URL.createObjectURL(file);
				element.download = "DefectParts.csv";
				document.body.appendChild(element);
				element.click();
				document.body.removeChild(element);
				URL.revokeObjectURL(element.href);
			}
		} catch {
			dispatch(showError(t("message.failedToDownloadTemplate")));
		}
	};

	// ── Upload + dry-run ─────────────────────────────────────────────────────

	const onDocumentUpload = async (key, url, res) => {
		isCancelled.current = false;
		setFileError(null);
		if (!res) {
			setUploadPercentCompleted(0);
			setLoading(false);
			return;
		}
		setFetchLoading(true);
		try {
			const response = await importDefectParts(defectId, {
				Key: key,
				Import: false,
			});
			if (isCancelled.current) return;
			if (response.status) {
				const rows = Array.isArray(response.data)
					? response.data
					: (response.data?.rows ?? []);
				const selectable = rows
					.map((_, i) => i)
					.filter((i) => !hasErrors(rows[i]));
				setPreviewRows(rows);
				setSelectedIndices(new Set(selectable));
				setS3Key(key);
				setView("preview");
			} else {
				setFileError(
					response?.data?.detail ?? t("validation.fileCouldNotBeProcessed")
				);
			}
		} catch {
			setFileError(t("validation.fileCouldNotBeProcessed"));
		} finally {
			setFetchLoading(false);
			setUploadPercentCompleted(0);
			setLoading(false);
			setShowProgress(true);
		}
	};

	// ── Confirm import ────────────────────────────────────────────────────────

	const handleConfirmImport = async () => {
		setFetchLoading(true);
		try {
			const response = await importDefectParts(defectId, {
				Key: s3Key,
				Import: true,
				SelectedIndices: [...selectedIndices].sort((a, b) => a - b),
				ManualRows: manualRows.map((row) => ({
					name: row.name,
					description: row.description,
					qty: Number(row.qty),
				})),
			});
			if (response.status) {
				await importSuccess();
				closeOverride();
			} else {
				dispatch(
					showError(response?.data?.detail || t("message.failedToImport"))
				);
			}
		} catch {
			dispatch(showError(t("message.failedToImport")));
		} finally {
			setFetchLoading(false);
		}
	};

	// ── CSV row selection ─────────────────────────────────────────────────────

	const selectableIndices = useMemo(
		() =>
			previewRows
				.map((_, i) => i)
				.filter((i) => !hasErrors(previewRows[i]) && !convertedCsvIndices.has(i)),
		[previewRows, convertedCsvIndices]
	);

	const handleToggleRow = (index) => {
		if (hasErrors(previewRows[index]) || convertedCsvIndices.has(index)) return;
		setSelectedIndices((prev) => {
			const next = new Set(prev);
			next.has(index) ? next.delete(index) : next.add(index);
			return next;
		});
	};

	const handleToggleAll = () => {
		setSelectedIndices((prev) =>
			prev.size === selectableIndices.length
				? new Set()
				: new Set(selectableIndices)
		);
	};

	// ── Inline editing ────────────────────────────────────────────────────────

	const startEditCsvRow = (index) => {
		const row = previewRows[index];
		setDraftValues({
			name: row.name ?? "",
			qty: row.qty ?? "",
			description: row.description ?? "",
		});
		setEditingRow({ source: "csv", index, isNew: false });
	};

	const startEditManualRow = (index) => {
		const row = manualRows[index];
		setDraftValues({
			name: row.name ?? "",
			qty: row.qty ?? "",
			description: row.description ?? "",
		});
		setEditingRow({ source: "manual", index, isNew: false });
	};

	const handleAddManualRow = () => {
		const newIndex = manualRows.length;
		setManualRows((prev) => [
			...prev,
			{ name: "", qty: "", description: "" },
		]);
		setDraftValues(EMPTY_DRAFT);
		setEditingRow({ source: "manual", index: newIndex, isNew: true });
	};

	const handleSaveEdit = () => {
		if (!editingRow) return;
		if (editingRow.source === "csv") {
			setManualRows((prev) => [...prev, { ...draftValues }]);
			setConvertedCsvIndices((prev) => new Set([...prev, editingRow.index]));
			setSelectedIndices((prev) => {
				const next = new Set(prev);
				next.delete(editingRow.index);
				return next;
			});
		} else {
			setManualRows((prev) =>
				prev.map((r, i) =>
					i === editingRow.index ? { ...draftValues } : r
				)
			);
		}
		setEditingRow(null);
		setDraftValues(EMPTY_DRAFT);
	};

	const handleCancelEdit = () => {
		if (editingRow?.isNew) {
			setManualRows((prev) => prev.slice(0, -1));
		}
		setEditingRow(null);
		setDraftValues(EMPTY_DRAFT);
	};

	const handleDeleteManualRow = (index) => {
		setManualRows((prev) => prev.filter((_, i) => i !== index));
		if (editingRow?.source === "manual") {
			if (editingRow.index === index) {
				setEditingRow(null);
				setDraftValues(EMPTY_DRAFT);
			} else if (index < editingRow.index) {
				setEditingRow((prev) => ({ ...prev, index: prev.index - 1 }));
			}
		}
	};

	// ── Cancel file upload ────────────────────────────────────────────────────

	const handleCancelFileUpload = () => {
		if (cancelFileUpload.current) {
			cancelFileUpload.current("Cancelled File import");
			isCancelled.current = true;
			dispatch(
				showNotications({
					show: true,
					message: t("message.cancelledFileImport"),
					severity: "error",
				})
			);
		}
	};

	useEffect(() => {
		if (uploadPercentCompleted === 100) setShowProgress(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadPercentCompleted]);

	// ── Derived flags ─────────────────────────────────────────────────────────

	const isEditing = editingRow !== null;
	const allSelected =
		selectableIndices.length > 0 &&
		selectedIndices.size === selectableIndices.length;
	const someSelected =
		selectedIndices.size > 0 &&
		selectedIndices.size < selectableIndices.length;
	const canImport =
		!isEditing && (selectedIndices.size > 0 || manualRows.length > 0);

	// ── Inline edit cells (shared between CSV and manual rows) ────────────────

	const renderEditCells = () => (
		<>
			<TableCell>
				<TextField
					size="small"
					type="number"
					inputProps={{ min: 1 }}
					value={draftValues.qty}
					onChange={(e) =>
						setDraftValues((prev) => ({ ...prev, qty: e.target.value }))
					}
					style={{ width: 80 }}
				/>
			</TableCell>
			<TableCell>
				<TextField
					size="small"
					value={draftValues.name}
					onChange={(e) =>
						setDraftValues((prev) => ({ ...prev, name: e.target.value }))
					}
					style={{ width: "100%" }}
				/>
			</TableCell>
			<TableCell>
				<TextField
					size="small"
					value={draftValues.description}
					onChange={(e) =>
						setDraftValues((prev) => ({
							...prev,
							description: e.target.value,
						}))
					}
					style={{ width: "100%" }}
				/>
			</TableCell>
			<TableCell>
				<div className={classes.actionCell}>
					<IconButton
						size="small"
						onClick={handleSaveEdit}
						disabled={!draftValues.name || !draftValues.qty || Number(draftValues.qty) < 1}
					>
						<CheckIcon fontSize="small" style={{ color: "#23BB79" }} />
					</IconButton>
					<IconButton size="small" onClick={handleCancelEdit}>
						<CloseIcon fontSize="small" style={{ color: "#E21313" }} />
					</IconButton>
				</div>
			</TableCell>
		</>
	);

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<>
			{fetchLoading && <LinearProgress className={classes.loading} />}
			<Dialog open={open} onClose={closeOverride} fullWidth maxWidth="md">
				<AT.ActionContainerFileUpload>
					<DialogTitle>
						<AT.HeaderText>
							{view === "upload"
								? t("title.uploadCsvFile")
								: t("title.selectPartsToImport")}
						</AT.HeaderText>
						{view === "upload" && (
							<AT.NoteText>{t("message.uploadCsvNote")}</AT.NoteText>
						)}
					</DialogTitle>
					<AT.NoteButtonContainer>
						<AT.CancelButton
							variant="contained"
							onClick={() => {
								if (view === "upload") handleCancelFileUpload();
								closeOverride();
							}}
						>
							{t("action.cancel")}
						</AT.CancelButton>
					</AT.NoteButtonContainer>
				</AT.ActionContainerFileUpload>

				<DialogContent>
					{view === "upload" && (
						<>
							<div className={classes.content}>
								<DropUpload
									filesUploading={loading}
									setFilesUploading={setLoading}
									isImageUploaded={false}
									uploadReturn={onDocumentUpload}
									apiPath={`${BASE_API_PATH}Defects/${defectId}/parts/upload`}
									showProgress={showProgress}
									uploadPercentCompleted={uploadPercentCompleted}
									setUploadPercentCompleted={setUploadPercentCompleted}
									percentMultiplyBy={100}
									cancelFileUpload={cancelFileUpload}
									isDocumentUploaded
									getError={(msg) => dispatch(showError(msg))}
								/>
								{fileError && (
									<div className={classes.fileError}>{fileError}</div>
								)}
							</div>
							<div style={{ height: "10px" }} />
							<Link
								onClick={downloadTemplate}
								style={{ cursor: "pointer", fontSize: "16px", color: "#307AD6" }}
							>
								{t("action.downloadImportTemplate")}
							</Link>
							<div style={{ height: "10px" }} />
						</>
					)}

					{view === "preview" && (
						<>
							<Table size="small">
								<TableHead className={classes.tableHead}>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												checked={allSelected}
												indeterminate={someSelected}
												onChange={handleToggleAll}
												disabled={
													selectableIndices.length === 0 || isEditing
												}
											/>
										</TableCell>
										<TableCell style={{ width: "90px" }}>
											{t("label.qty")}
										</TableCell>
										<TableCell style={{ width: "180px" }}>
											{t("label.name")}
										</TableCell>
										<TableCell>{t("label.description")}</TableCell>
										<TableCell style={{ width: "72px" }} />
									</TableRow>
								</TableHead>
								<TableBody>
									{/* CSV rows */}
									{previewRows.map((row, i) => {
										if (convertedCsvIndices.has(i)) return null;

										const rowHasErrors = hasErrors(row);
										const rowHasWarnings = hasWarnings(row);
										const isCsvEditing =
											editingRow?.source === "csv" &&
											editingRow?.index === i;

										if (isCsvEditing) {
											return (
												<TableRow key={i} className={classes.editingRow}>
													<TableCell padding="checkbox" />
													{renderEditCells()}
												</TableRow>
											);
										}

										return (
											<TableRow
												key={i}
												hover={!rowHasErrors && !isEditing}
												onClick={
													!rowHasErrors && !isEditing
														? () => handleToggleRow(i)
														: undefined
												}
												className={
													rowHasErrors
														? classes.rowError
														: rowHasWarnings
														? classes.rowWarning
														: undefined
												}
												style={{
													cursor:
														rowHasErrors || isEditing
															? "default"
															: "pointer",
												}}
											>
												<TableCell padding="checkbox">
													<Checkbox
														checked={selectedIndices.has(i)}
														disabled={rowHasErrors || isEditing}
														onChange={() => handleToggleRow(i)}
														onClick={(e) => e.stopPropagation()}
													/>
												</TableCell>
												<TableCell>{row.qty}</TableCell>
												<TableCell>{row.name}</TableCell>
												<TableCell>{row.description}</TableCell>
												<TableCell>
													<div className={classes.actionCell}>
														{rowHasErrors && (
															<ErrorMessageWithErrorIcon
																message={row.errors}
															/>
														)}
														{!rowHasErrors && rowHasWarnings && (
															<WarningMessageWithErrorIcon
																message={row.warnings}
															/>
														)}
														{!rowHasErrors && (
															<IconButton
																size="small"
																disabled={isEditing}
																onClick={(e) => {
																	e.stopPropagation();
																	startEditCsvRow(i);
																}}
															>
																<EditOutlinedIcon
																	fontSize="small"
																	style={{ color: "#307AD6" }}
																/>
															</IconButton>
														)}
													</div>
												</TableCell>
											</TableRow>
										);
									})}

									{/* Manual rows section header */}
									{manualRows.length > 0 && (
										<TableRow className={classes.sectionRow}>
											<TableCell
												colSpan={5}
												className={classes.sectionLabel}
											>
												{t("label.manualEntries")}
											</TableCell>
										</TableRow>
									)}

									{/* Manual rows */}
									{manualRows.map((row, i) => {
										const isManualEditing =
											editingRow?.source === "manual" &&
											editingRow?.index === i;

										if (isManualEditing) {
											return (
												<TableRow
													key={`manual-${i}`}
													className={classes.editingRow}
												>
													<TableCell padding="checkbox" />
													{renderEditCells()}
												</TableRow>
											);
										}

										return (
											<TableRow key={`manual-${i}`}>
												<TableCell padding="checkbox">
													<span style={manualPillStyle}>
														{t("label.manual")}
													</span>
												</TableCell>
												<TableCell>{row.qty}</TableCell>
												<TableCell>{row.name}</TableCell>
												<TableCell>{row.description}</TableCell>
												<TableCell>
													<div className={classes.actionCell}>
														<IconButton
															size="small"
															disabled={isEditing}
															onClick={() => startEditManualRow(i)}
														>
															<EditOutlinedIcon
																fontSize="small"
																style={{ color: "#307AD6" }}
															/>
														</IconButton>
														<IconButton
															size="small"
															disabled={isEditing}
															onClick={() => handleDeleteManualRow(i)}
														>
															<DeleteOutlinedIcon
																fontSize="small"
																style={{ color: "#E21313" }}
															/>
														</IconButton>
													</div>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>

							<Button
								className={classes.addRowButton}
								disabled={isEditing}
								onClick={handleAddManualRow}
								variant="text"
							>
								{t("action.addRow")}
							</Button>

							<div className={classes.previewActions}>
								<span className={classes.selectionCount}>
									{t("label.csvRowsSelected", {
										count: selectedIndices.size,
									})}
									{manualRows.length > 0 && (
										<>
											{" + "}
											{t("label.countManual", {
												count: manualRows.length,
											})}
										</>
									)}
									{previewRows.length -
										selectableIndices.length -
										convertedCsvIndices.size >
										0 && (
										<>
											{" — "}
											{t("label.rowsWithErrors", {
												count:
													previewRows.length -
													selectableIndices.length -
													convertedCsvIndices.size,
											})}
										</>
									)}
								</span>
								<AT.CancelButton
									variant="contained"
									onClick={() => {
										setView("upload");
										setFileError(null);
									}}
								>
									{t("action.back")}
								</AT.CancelButton>
								<AT.ConfirmButton
									variant="contained"
									disabled={!canImport}
									onClick={handleConfirmImport}
								>
									{t("action.import")} (
									{selectedIndices.size + manualRows.length})
								</AT.ConfirmButton>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ImportPartsDialog;
